import { useCallback, useState } from "react";

import { Insight } from "models/insight";

import { fetchApi } from "utils/baseFetcher";

import useAuthentication from "./useAuthentication";
import { useGlobalState } from "./useGlobalState";

export const useInsightInteractions = ({
  insight,
  lockerItem: inputLockerItem,
  source,
}: {
  insight: Insight;
  lockerItem?: any;
  source?: "snippet" | "longform";
}) => {
  const { account: loggedInAccount } = useAuthentication();

  const {
    id,
    myLikeScore,
    entity,
    tagline,
    byline,
    lockerItems,
    shareableLinks: links,
    primaryEntity,
    contentVertical,
    account,
  } = insight;

  // like/dislike
  const canLike = loggedInAccount?.id !== account?.id;
  const showLike = canLike || loggedInAccount?.id === account?.id; // it's redundant but keep it similar to ember implementation
  const canDislike = canLike;
  const [isLiked, setIsLiked] = useGlobalState(
    `insight-like-${id}`,
    myLikeScore === 1
  );
  const [isDisliked, setIsDisliked] = useGlobalState(
    `insight-dislike-${id}`,
    myLikeScore === -1
  );
  const like = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation?.();

      const action = isLiked ? "unlike" : "like";
      setIsLiked(!isLiked);
      if (!isLiked && isDisliked) {
        setIsDisliked(!isDisliked);
      }
      fetchApi(`/api/v2/insights/${id}/${action}`, {
        method: "PATCH",
        body: JSON.stringify({
          "reason-ids": ["1"],
          source,
        }),
      })
        .then(() => {})
        .catch(() => {
          setIsLiked(!isLiked);
          if (!isLiked && !isDisliked) {
            setIsDisliked(!isDisliked);
          }
        });
    },
    [id, isDisliked, isLiked, setIsDisliked, setIsLiked, source]
  );

  const dislike = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation?.();

      const action = isDisliked ? "unlike" : "dislike";
      setIsDisliked(!isDisliked);
      if (!isDisliked && isLiked) {
        setIsLiked(!isLiked);
      }
      fetchApi(`/api/v2/insights/${id}/${action}`, {
        method: "PATCH",
        body: JSON.stringify({
          "reason-ids": ["1"],
          source,
        }),
      })
        .then(() => {})
        .catch(() => {
          setIsDisliked(!isDisliked);
          if (!isDisliked && !isLiked) {
            setIsLiked(!isLiked);
          }
        });
    },
    [id, isDisliked, isLiked, setIsDisliked, setIsLiked, source]
  );

  // save
  const [lockerItem, setLockerItem] = useState(
    inputLockerItem || lockerItems?.[0]
  );
  const [isSaved, setIsSaved] = useState(
    inputLockerItem || !!lockerItems?.length
  );
  const [isSaving, setIsSaving] = useState(false);

  const save = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();

      if (isSaving) return;

      setIsSaving(true);
      setIsSaved(!isSaved);
      if (isSaved) {
        fetchApi(`/api/v2/locker-items/${lockerItem.id}`, {
          method: "DELETE",
        }).finally(() => {
          setIsSaving(false);
        });
      } else {
        fetchApi(`/api/v2/locker-items`, {
          method: "POST",
          headers: { "content-type": "application/vnd.api+json" },
          body: JSON.stringify({
            data: {
              attributes: {
                title: tagline,
                reference: null,
                "item-type": "insight",
                note: null,
                "access-level": "private",
                "custom-keywords": [],
              },
              relationships: {
                asset: { data: { type: "insights", id: insight?.id } },
                ...(entity
                  ? {
                      entities: {
                        data: [{ type: "entities", id: entity.id }],
                      },
                    }
                  : {}),
              },
              type: "locker-items",
            },
            include: "asset,entities,account",
          }),
        })
          .then((newLockerItem) => {
            setLockerItem(newLockerItem);
          })
          .catch(() => {
            setIsSaved(!isSaved);
          })
          .finally(() => {
            setIsSaving(false);
          });
      }
    },
    [isSaving, isSaved, lockerItem?.id, tagline, insight?.id, entity]
  );

  // share
  const shareOptions = {
    email: {
      link: `mailto:?subject=\u272a Smartkarma | ${encodeURIComponent(
        tagline
      )}&body=${encodeURIComponent(
        `${tagline}\n${primaryEntity?.prettyName} in ${contentVertical?.name}\n\n${byline}\n\nRead more: ${links?.email}`
      )}`,
    },
    clipboard: {
      message: `${tagline}. Read more: ${links?.direct}`,
    },
    privateMessage: {
      message: `${tagline}. Read more: <a href="${links?.direct}">${links?.direct}</a>`,
    },
    whatsapp: {
      link: `https://web.whatsapp.com/send?text=${tagline}.%20%0ARead more: ${links?.whatsapp}`,
    },
    twitter: {
      link: `https://twitter.com/share?url=${links?.twitter}&text=${tagline}&via=smartkarma`,
    },
    linkedIn: {
      link: `https://www.linkedin.com/shareArticle?mini=true&url=${links?.linkedin}&source=Smartkarma`,
    },
  };

  return {
    showLike,
    canLike,
    canDislike,
    isLiked,
    isDisliked,
    like,
    dislike,
    isSaved,
    isSaving,
    save,
    shareOptions,
  };
};
